import { sceneSymbolRequests } from '../../symbols/scene';
import { projectWithTargetGraphic, targetGraphic } from '../../symbols/targets';
import { isCapabilityAvailable } from '../../licensing/entitlements';
import { LatestOperationCoordinator } from '../../operations/latestOperation';
import { importSymbolFile } from '../../symbols/import';
import { SymbolImageCache } from '../../symbols/images';
import { symbolName, equalBytes, SymbolImportError } from '../../symbols/model';
import { addSymbolAsset, symbolUsage, validateRegistry } from '../../symbols/registry';
import { ACTION_OK, actionError, type AppStoreActionContext } from './actionSupport';
import type { AppState } from './appState';

export type SymbolActions = Pick<
  AppState,
  | 'importSymbolForTarget'
  | 'importPointSymbol'
  | 'cancelSymbolImport'
  | 'renameSymbolAsset'
  | 'removeSymbolAsset'
>;
export function createSymbolActions({ get, set }: AppStoreActionContext): SymbolActions {
  const operation = new LatestOperationCoordinator();
  const canAuthor = () =>
    get().mode === 'editor' && isCapabilityAvailable(get().entitlement, 'symbols.import');
  return {
    cancelSymbolImport: () => {
      if (operation.currentId !== null) operation.cancel(operation.currentId);
      set({ symbolImportBusy: false, symbolImportMessage: '' });
    },
    importPointSymbol: (layerId, file, replaceId, confirmedUses) =>
      get().importSymbolForTarget({ kind: 'point', layerId }, file, replaceId, confirmedUses),
    importSymbolForTarget: async (target, file, replaceId, confirmedUses = 0) => {
      const layerId = target.layerId;
      const targetSelected = () =>
        target.kind === 'point'
          ? get().shell.selectedLayerId === layerId
          : get().shell.activeAnnotationLayerId === layerId &&
            get().shell.selectedAnnotationId === target.annotationId;
      const targetAllowed = () =>
        target.kind === 'point' || isCapabilityAvailable(get().entitlement, 'annotations.author');
      if (!canAuthor() || !targetAllowed())
        return actionError('Custom symbol authoring is unavailable here.');
      const before = get(),
        sources = before.project.sources,
        library = before.project.symbolAssets;
      const originalGraphic = targetGraphic(before.project, target);
      if (!originalGraphic || !targetSelected())
        return actionError('Select the target point layer first.');
      const uses = replaceId ? symbolUsage(before.project, replaceId).length : 0;
      if (uses > 1 && confirmedUses < uses)
        return actionError(`Confirm replacement everywhere this symbol is used (${uses} styles).`);
      if (replaceId && !library.some((a) => a.id === replaceId))
        return actionError('This symbol is no longer available.');
      const pending = operation.start(),
        cache = new SymbolImageCache();
      set({ symbolImportBusy: true, symbolImportMessage: 'Preparing symbol…' });
      try {
        const imported = await importSymbolFile(file, pending.signal);
        await cache.prepare(imported.asset, 2, pending.signal);
        const state = get();
        if (
          !operation.isCurrent(pending.id) ||
          state.project.sources !== sources ||
          state.project.symbolAssets !== library ||
          !targetSelected() ||
          targetGraphic(state.project, target) !== originalGraphic ||
          !canAuthor() ||
          !targetAllowed()
        )
          return actionError('Symbol import cancelled because the project or target changed.');
        let project = state.project,
          duplicate = false;
        if (replaceId) {
          const currentUses = symbolUsage(project, replaceId).length;
          if (currentUses > 1 && confirmedUses < currentUses)
            throw new SymbolImportError(
              `Confirm replacement everywhere this symbol is used (${currentUses} styles).`,
            );
          if (
            library.some(
              (a) =>
                a.id !== replaceId &&
                a.mimeType === imported.asset.mimeType &&
                a.sha256 === imported.asset.sha256 &&
                equalBytes(a.bytes, imported.asset.bytes),
            )
          )
            throw new SymbolImportError(
              'This artwork is already another library symbol. Select that symbol instead of replacing this one.',
            );
          const original = library.find((a) => a.id === replaceId)!;
          const assets = library.map((a) =>
            a.id === replaceId ? { ...imported.asset, id: replaceId, name: original.name } : a,
          );
          validateRegistry(assets);
          project = { ...project, symbolAssets: assets };
        } else {
          const added = addSymbolAsset(library, imported.asset);
          duplicate = added.duplicate;
          project = projectWithTargetGraphic({ ...project, symbolAssets: added.assets }, target, {
            kind: 'asset',
            assetId: added.asset.id,
          });
        }
        await cache.catalogue(
          project.symbolAssets,
          Math.min(4, Math.max(2, window.devicePixelRatio || 1)),
          pending.signal,
        );
        await cache.graphics(
          sceneSymbolRequests(project.layers, project.annotationLayers),
          project.symbolAssets,
          Math.min(4, Math.max(2, window.devicePixelRatio || 1)),
          pending.signal,
        );
        if (
          !operation.isCurrent(pending.id) ||
          get().project.sources !== sources ||
          get().project.symbolAssets !== library ||
          get().project.layers !== state.project.layers ||
          get().project.annotationLayers !== state.project.annotationLayers ||
          !targetSelected() ||
          !targetAllowed() ||
          !canAuthor()
        )
          return actionError('Symbol import cancelled because the project or target changed.');
        project = {
          ...get().project,
          symbolAssets: project.symbolAssets,
          layers: project.layers,
          annotationLayers: project.annotationLayers,
        };
        set({
          project,
          symbolImportMessage: duplicate
            ? 'This symbol is already in your project. Selected the existing symbol.'
            : (imported.notice ??
              (replaceId ? 'Symbol replaced everywhere.' : 'Symbol imported and selected.')),
        });
        return ACTION_OK;
      } catch (error) {
        const message = pending.signal.aborted
          ? 'Symbol import cancelled.'
          : error instanceof Error && error.name === 'SymbolImportError'
            ? error.message
            : 'This symbol could not be imported. Try a supported SVG or transparent PNG.';
        if (operation.isCurrent(pending.id)) set({ symbolImportMessage: message });
        return actionError(message);
      } finally {
        cache.clear();
        if (operation.finish(pending.id)) set({ symbolImportBusy: false });
      }
    },
    renameSymbolAsset: (id, name) => {
      if (!canAuthor()) return actionError('Symbol authoring is unavailable here.');
      try {
        const project = get().project;
        if (!project.symbolAssets.some((a) => a.id === id))
          return actionError('This symbol is unavailable.');
        const normalized = symbolName(name);
        set({
          project: {
            ...project,
            symbolAssets: project.symbolAssets.map((a) =>
              a.id === id ? { ...a, name: normalized } : a,
            ),
          },
        });
        return ACTION_OK;
      } catch (error) {
        return actionError(error instanceof Error ? error.message : 'Use a valid name.');
      }
    },
    removeSymbolAsset: (id) => {
      if (!canAuthor()) return actionError('Symbol authoring is unavailable here.');
      const project = get().project,
        uses = symbolUsage(project, id);
      if (uses.length)
        return actionError(
          `This symbol is used by ${uses.length} styles and cannot be removed until those references are changed.`,
        );
      set({
        project: { ...project, symbolAssets: project.symbolAssets.filter((a) => a.id !== id) },
      });
      return ACTION_OK;
    },
  };
}
